import { useEffect, useId, useMemo, useState } from 'react';
import { useT } from '../../i18n';
import { safeId } from '../ui/labelledControl';
import { nativeInfo } from '../../lib/nativeBridge';
import {
  browserProbeHost,
  ceremonyHint,
  probeReport,
  readCapabilities,
  readEnvironment,
  rpIdCandidates,
  runCeremony,
  type CapabilityReport,
  type CeremonyKind,
  type CeremonyReport,
  type EnvironmentReport,
  type Probe,
  type ProbeHost,
} from '../../lib/passkeyProbe';

// ─────────────────────────────────────────────────────────────────────
// /diagnostics/passkeys — the WebAuthn probe (#676).
//
// For MEASUREMENT, not for members. It answers «can the native app's WebView
// run a passkey ceremony?» from inside that WebView, and shows the raw answer
// for the owner to copy onto the issue. The logic and its reasons are in
// lib/passkeyProbe.ts; this file only lays the readings out.
//
// Reached in the app only by the non-production `passkey-probe` EAS build,
// which starts here (docs/domains/MOBILE.md § Passkeys in the WebView). A
// browser can open it too, which is useful as the control on the same phone.
// ─────────────────────────────────────────────────────────────────────

export interface PasskeyProbeProps {
  /** Injected by tests; the real page reads `window`. */
  host?: ProbeHost;
}

export function PasskeyProbe({ host: injected }: PasskeyProbeProps) {
  const t = useT();
  const auto = useId();
  const id = (name: string) => safeId(`probe-${name}`, auto);
  const host = useMemo(() => injected ?? browserProbeHost(), [injected]);
  const [environment, setEnvironment] = useState<EnvironmentReport | null>(null);
  const [capabilities, setCapabilities] = useState<CapabilityReport | null>(null);
  const [ceremonies, setCeremonies] = useState<CeremonyReport[]>([]);
  const [running, setRunning] = useState<string | null>(null);
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    let live = true;
    void Promise.all([readEnvironment(host), readCapabilities(host)]).then(([env, caps]) => {
      if (!live) return;
      setEnvironment(env);
      setCapabilities(caps);
    });
    return () => {
      live = false;
    };
  }, [host]);

  const candidates = rpIdCandidates(host.location.hostname);
  const shell = nativeInfo();
  const report = JSON.stringify(
    probeReport({ shell, environment, capabilities, ceremonies }),
    null,
    2,
  );

  const run = async (kind: CeremonyKind, rpId: string) => {
    setRunning(`${kind}:${rpId}`);
    const result = await runCeremony(kind, rpId, host);
    setCeremonies((prev) => [...prev, result]);
    setRunning(null);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(report);
      setCopied('copied');
    } catch {
      setCopied('failed');
    }
  };

  const probeText = (p: Probe<unknown> | undefined): string => {
    if (!p) return t('probe.reading');
    if (p.kind === 'absent') return t('probe.absent');
    if (p.kind === 'threw') return t('probe.threw', { name: p.name, message: p.message });
    return typeof p.value === 'object' ? JSON.stringify(p.value) : String(p.value);
  };
  const yesNo = (v: boolean) => (v ? t('probe.yes') : t('probe.no'));

  return (
    <main className="min-h-screen bg-paper px-4 py-6 text-ink">
      <div className="mx-auto max-w-lg space-y-4">
        <header className="space-y-1">
          <h1 className="font-display text-xl font-semibold">{t('probe.title')}</h1>
          <p className="text-sm text-ink-2 leading-relaxed">{t('probe.lead')}</p>
        </header>

        <section className="card p-4 space-y-2" aria-labelledby={id('env')}>
          <h2 id={id('env')} className="font-semibold">
            {t('probe.env.title')}
          </h2>
          <dl className="text-sm space-y-1">
            <Row label={t('probe.env.origin')} value={host.location.origin} />
            <Row
              label={t('probe.env.shell')}
              value={shell ? `${shell.platform} · ${shell.appVersion}` : t('probe.env.noShell')}
            />
            <Row
              label={t('probe.env.webView')}
              value={environment ? yesNo(environment.androidWebView) : t('probe.reading')}
            />
            <Row
              label={t('probe.env.chromium')}
              value={environment?.chromiumVersion ?? t('probe.unknown')}
            />
            <Row
              label={t('probe.env.android')}
              value={environment?.androidVersion ?? t('probe.unknown')}
            />
            <Row label={t('probe.env.highEntropy')} value={probeText(environment?.highEntropy)} />
            <Row label={t('probe.env.userAgent')} value={host.navigator.userAgent} />
          </dl>
        </section>

        <section className="card p-4 space-y-2" aria-labelledby={id('caps')}>
          <h2 id={id('caps')} className="font-semibold">
            {t('probe.caps.title')}
          </h2>
          <dl className="text-sm space-y-1">
            <Row
              label={t('probe.caps.publicKeyCredential')}
              value={capabilities?.publicKeyCredential ?? t('probe.reading')}
            />
            <Row
              label={t('probe.caps.credentials')}
              value={
                capabilities
                  ? `create: ${yesNo(capabilities.credentialsCreate)} · get: ${yesNo(capabilities.credentialsGet)}`
                  : t('probe.reading')
              }
            />
            <Row
              label={t('probe.caps.platformAuthenticator')}
              value={probeText(capabilities?.platformAuthenticator)}
            />
            <Row
              label={t('probe.caps.conditionalMediation')}
              value={probeText(capabilities?.conditionalMediation)}
            />
            <Row
              label={t('probe.caps.clientCapabilities')}
              value={probeText(capabilities?.clientCapabilities)}
            />
          </dl>
        </section>

        <section className="card p-4 space-y-3" aria-labelledby={id('ceremony')}>
          <h2 id={id('ceremony')} className="font-semibold">
            {t('probe.ceremony.title')}
          </h2>
          <p className="text-sm text-ink-2 leading-relaxed">{t('probe.ceremony.lead')}</p>
          <div className="flex flex-col gap-2">
            {candidates.map((rpId) => (
              <button
                key={`get:${rpId}`}
                type="button"
                className="btn-outline"
                disabled={running !== null}
                onClick={() => void run('get', rpId)}
              >
                {t('probe.ceremony.get', { rpId })}
              </button>
            ))}
            {candidates[0] && (
              <button
                type="button"
                className="btn-outline"
                disabled={running !== null}
                onClick={() => void run('create', candidates[0])}
              >
                {t('probe.ceremony.create', { rpId: candidates[0] })}
              </button>
            )}
          </div>
          <p className="text-xs rounded-lg px-3 py-2 bg-amber-50 text-amber-900">
            {t('probe.ceremony.createWarning')}
          </p>
          {running && <p className="text-sm text-ink-2">{t('probe.ceremony.running')}</p>}
          <ol className="space-y-2">
            {ceremonies.map((c, i) => (
              <li key={i} className="text-sm">
                <p className="font-semibold">
                  {c.ceremony} · {c.rpId} · {c.elapsedMs} ms
                </p>
                <p className="font-mono break-all">
                  {c.outcome === 'error'
                    ? `${c.errorName}: ${c.errorMessage}`
                    : c.outcome === 'completed'
                      ? `${t('probe.ceremony.completed')} ${JSON.stringify(c.clientData ?? null)}`
                      : t('probe.ceremony.unavailable')}
                </p>
                <p className="text-ink-2">{t(`probe.hint.${ceremonyHint(c)}`)}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="card p-4 space-y-2" aria-labelledby={id('report')}>
          <h2 id={id('report')} className="font-semibold">
            {t('probe.report.title')}
          </h2>
          <label htmlFor={id('report-json')} className="text-sm text-ink-2 block">
            {t('probe.report.lead')}
          </label>
          <textarea
            id={id('report-json')}
            readOnly
            value={report}
            rows={10}
            className="w-full font-mono text-xs p-2 rounded-lg bg-paper"
            onFocus={(e) => e.currentTarget.select()}
          />
          <button type="button" className="btn-primary" onClick={() => void copy()}>
            {t('probe.report.copy')}
          </button>
          {copied !== 'idle' && (
            <p className="text-sm text-ink-2" role="status">
              {copied === 'copied' ? t('probe.report.copied') : t('probe.report.copyFailed')}
            </p>
          )}
        </section>

        {/* The app's WebView has no reload gesture. The native opt-in is applied
            on the WebView's first layout, so a reading taken on the very first
            load is worth repeating once before it is believed (app.config.ts). */}
        <button type="button" className="btn-outline" onClick={() => window.location.reload()}>
          {t('probe.reload')}
        </button>
        <a href="/?native=1#mobile" className="text-sm underline block">
          {t('probe.back')}
        </a>
      </div>
    </main>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-ink-2">{label}</dt>
      <dd className="font-mono break-all">{value}</dd>
    </div>
  );
}
